import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = {
  key: string;
  label: string;
  pvs: string;
  pvsSecondary: string;
  timPrimary: string;
  timSecondary: string;
  sidebarBg: string;
  sidebarBorder: string;
  sidebarActiveBg: string;
  appBg: string;
};

export const themes: Theme[] = [
  {
    key: 'protan',
    label: 'Protan (Rotschwäche)',
    pvs: '#0072B2',
    pvsSecondary: '#F0E442',
    timPrimary: '#56B4E9',
    timSecondary: '#F5EE7A',
    sidebarBg: '#0b2a3d',
    sidebarBorder: '#124663',
    sidebarActiveBg: '#124663',
    appBg: '#f2f6fa',
  },
  {
    key: 'deutan',
    label: 'Deutan (Grünschwäche)',
    pvs: '#005AB5',
    pvsSecondary: '#E69F00',
    timPrimary: '#3C8DD9',
    timSecondary: '#F0B833',
    sidebarBg: '#0a1f3d',
    sidebarBorder: '#143566',
    sidebarActiveBg: '#143566',
    appBg: '#f3f5fa',
  },
  {
    key: 'tritan',
    label: 'Tritan (Blauschwäche)',
    pvs: '#D55E00',
    pvsSecondary: '#009E73',
    timPrimary: '#E88B4A',
    timSecondary: '#4DB89A',
    sidebarBg: '#3d1f0b',
    sidebarBorder: '#6b3a1a',
    sidebarActiveBg: '#6b3a1a',
    appBg: '#faf4ef',
  },
];

const STORAGE_KEY = 'pvs_theme';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (key: string) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: themes[0],
  setTheme: () => {},
});

function applyCssVars(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--primary', theme.pvs);
  root.style.setProperty('--secondary', theme.pvsSecondary);
  root.style.setProperty('--sidebar-bg', theme.sidebarBg);
  root.style.setProperty('--sidebar-border', theme.sidebarBorder);
  root.style.setProperty('--sidebar-active', theme.sidebarActiveBg);
  root.style.setProperty('--app-bg', theme.appBg);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeKey, setThemeKey] = useState<string>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || themes[0].key;
    } catch {
      return themes[0].key;
    }
  });

  const theme = themes.find(t => t.key === themeKey) || themes[0];

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, themeKey);
    } catch {}
    applyCssVars(theme);
  }, [themeKey, theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeKey }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
